import React, { useState, useEffect, useCallback } from 'react';
import { QrReader } from 'react-qr-reader';
import { 
  LogIn, LogOut, QrCode, MapPin, Phone, Navigation,
  CheckCircle, AlertTriangle, Truck, RefreshCw, X,
  Package, Clock, User, ChevronRight, ExternalLink,
  AlertCircle, Home, History, Calendar, Route, Unlock,
  FileText, Euro, Filter
} from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";

const API_BASE = process.env.REACT_APP_BACKEND_URL || '';

// Storage Keys
const STORAGE_KEYS = {
  TOKEN: 'flotto_driver_token',
  DRIVER_ID: 'flotto_driver_id',
  DRIVER_NAME: 'flotto_driver_name'
};

// Problem Reasons
const PROBLEM_REASONS = [
  { code: 'nobody_home', label: 'Niemand zu Hause' },
  { code: 'wrong_address', label: 'Falsche Adresse' },
  { code: 'customer_refused', label: 'Kunde verweigert Annahme' },
  { code: 'damaged_goods', label: 'Ware beschädigt' },
  { code: 'other', label: 'Sonstiges' }
];

// Release Reasons (für Lieferung freigeben)
const RELEASE_REASONS = [
  { code: 'wrong_order', label: 'Falsche Bestellung erhalten' },
  { code: 'vehicle_problem', label: 'Fahrzeugproblem' },
  { code: 'personal_emergency', label: 'Persönlicher Notfall' },
  { code: 'route_too_far', label: 'Route zu weit' },
  { code: 'other', label: 'Sonstiges' }
];

export default function FlottoApp() {
  // Auth State
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [driverId, setDriverId] = useState('');
  const [driverName, setDriverName] = useState('');
  const [password, setPassword] = useState('');
  const [authToken, setAuthToken] = useState('');
  const [loginLoading, setLoginLoading] = useState(false);
  
  // Scanner State
  const [scannerActive, setScannerActive] = useState(false);
  const [scanResult, setScanResult] = useState(null);
  const [scanning, setScanning] = useState(false);
  
  // Orders State
  const [orders, setOrders] = useState([]);
  const [loadingOrders, setLoadingOrders] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);
  
  // History State (NEU)
  const [historyOrders, setHistoryOrders] = useState([]);
  const [historyStats, setHistoryStats] = useState({ delivered: 0, problems: 0, total_revenue: 0 });
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [historyDateFrom, setHistoryDateFrom] = useState(new Date().toISOString().split('T')[0]);
  const [historyDateTo, setHistoryDateTo] = useState(new Date().toISOString().split('T')[0]);
  
  // Route Optimization State (NEU)
  const [showRouteDialog, setShowRouteDialog] = useState(false);
  const [optimizedRoute, setOptimizedRoute] = useState(null);
  const [loadingRoute, setLoadingRoute] = useState(false);
  
  // Problem Dialog
  const [showProblemDialog, setShowProblemDialog] = useState(false);
  const [problemOrderId, setProblemOrderId] = useState(null);
  const [selectedProblemReason, setSelectedProblemReason] = useState('');
  
  // Release Dialog (NEU)
  const [showReleaseDialog, setShowReleaseDialog] = useState(false);
  const [releaseOrderId, setReleaseOrderId] = useState(null);
  const [selectedReleaseReason, setSelectedReleaseReason] = useState('');
  const [releaseNotes, setReleaseNotes] = useState('');
  
  // Claim Dialog
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [claimedOrder, setClaimedOrder] = useState(null);
  
  // Active Tab
  const [activeTab, setActiveTab] = useState('active');

  // Check saved session on mount
  useEffect(() => {
    const savedToken = localStorage.getItem(STORAGE_KEYS.TOKEN);
    const savedDriverId = localStorage.getItem(STORAGE_KEYS.DRIVER_ID);
    const savedDriverName = localStorage.getItem(STORAGE_KEYS.DRIVER_NAME);
    
    if (savedToken && savedDriverId) {
      setAuthToken(savedToken);
      setDriverId(savedDriverId);
      setDriverName(savedDriverName || savedDriverId);
      setIsLoggedIn(true);
    }
  }, []);

  // Load orders when logged in
  useEffect(() => {
    if (isLoggedIn && authToken) {
      loadMyOrders();
    }
  }, [isLoggedIn, authToken]);

  const getAuthHeaders = () => ({
    'Content-Type': 'application/json',
    'Authorization': `Bearer ${authToken}`
  });

  // ==========================================================================
  // API Functions
  // ==========================================================================

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginLoading(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/driver/auth/login`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ driver_id: driverId, password })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Login fehlgeschlagen');
      }
      
      // Save to localStorage
      localStorage.setItem(STORAGE_KEYS.TOKEN, data.token);
      localStorage.setItem(STORAGE_KEYS.DRIVER_ID, data.driver_id);
      localStorage.setItem(STORAGE_KEYS.DRIVER_NAME, data.driver_name);
      
      setAuthToken(data.token);
      setDriverName(data.driver_name);
      setIsLoggedIn(true);
      setPassword('');
      
      toast.success(data.message || `Willkommen, ${data.driver_name}!`);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoginLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await fetch(`${API_BASE}/api/driver/auth/logout`, {
        method: 'POST',
        headers: getAuthHeaders()
      });
    } catch (error) {
      console.error('Logout error:', error);
    }
    
    // Clear local storage
    localStorage.removeItem(STORAGE_KEYS.TOKEN);
    localStorage.removeItem(STORAGE_KEYS.DRIVER_ID);
    localStorage.removeItem(STORAGE_KEYS.DRIVER_NAME);
    
    setIsLoggedIn(false);
    setAuthToken('');
    setDriverId('');
    setDriverName('');
    setOrders([]);
    setHistoryOrders([]);
    
    toast.success('Erfolgreich abgemeldet');
  };

  const loadMyOrders = useCallback(async () => {
    if (!authToken) return;
    setLoadingOrders(true);
    
    try {
      const response = await fetch(`${API_BASE}/api/driver/orders/my`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      const data = await response.json();
      setOrders(data.orders || []);
    } catch (error) {
      console.error('Load orders error:', error);
      toast.error('Fehler beim Laden der Lieferungen');
    } finally {
      setLoadingOrders(false);
    }
  }, [authToken]);

  // NEU: Historie laden
  const loadHistory = useCallback(async () => {
    if (!authToken) return;
    setLoadingHistory(true);
    
    try {
      const params = new URLSearchParams({
        date_from: historyDateFrom,
        date_to: historyDateTo
      });
      
      const response = await fetch(`${API_BASE}/api/driver/orders/history?${params}`, {
        headers: getAuthHeaders()
      });
      
      if (response.status === 401) {
        handleLogout();
        return;
      }
      
      const data = await response.json();
      setHistoryOrders(data.orders || []);
      setHistoryStats(data.stats || { delivered: 0, problems: 0, total_revenue: 0 });
    } catch (error) {
      console.error('Load history error:', error);
      toast.error('Fehler beim Laden der Historie');
    } finally {
      setLoadingHistory(false);
    }
  }, [authToken, historyDateFrom, historyDateTo]);

  // Load history when tab changes or dates change
  useEffect(() => {
    if (activeTab === 'history' && isLoggedIn) {
      loadHistory();
    }
  }, [activeTab, historyDateFrom, historyDateTo, isLoggedIn, loadHistory]);

  const claimOrder = async (orderId, qrToken) => {
    try {
      const response = await fetch(`${API_BASE}/api/driver/orders/${orderId}/claim`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ qr_token: qrToken })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Übernahme fehlgeschlagen');
      }
      
      setClaimedOrder(data.order);
      setShowClaimDialog(true);
      toast.success('Lieferung erfolgreich übernommen!');
      loadMyOrders();
      return data.order;
    } catch (error) {
      toast.error(error.message);
      throw error;
    }
  };

  const updateOrderStatus = async (orderId, status, problemReason = null) => {
    try {
      const body = { status };
      if (problemReason) body.problem_reason = problemReason;
      
      const response = await fetch(`${API_BASE}/api/driver/orders/${orderId}/status`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(body)
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Status-Update fehlgeschlagen');
      }
      
      toast.success(data.message);
      loadMyOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // NEU: Lieferung freigeben
  const releaseOrder = async () => {
    if (!releaseOrderId || !selectedReleaseReason) return;
    
    try {
      const response = await fetch(`${API_BASE}/api/driver/orders/${releaseOrderId}/release`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          reason: selectedReleaseReason,
          notes: releaseNotes 
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Freigabe fehlgeschlagen');
      }
      
      toast.success(data.message);
      setShowReleaseDialog(false);
      setReleaseOrderId(null);
      setSelectedReleaseReason('');
      setReleaseNotes('');
      loadMyOrders();
    } catch (error) {
      toast.error(error.message);
    }
  };

  // NEU: Multi-Route optimieren
  const optimizeRoute = async () => {
    if (orders.length < 1) {
      toast.error('Mindestens 1 Lieferung benötigt');
      return;
    }
    
    setLoadingRoute(true);
    
    try {
      const orderIds = orders.map(o => o.id || o._id);
      
      // Try to get current location
      let startLocation = null;
      if (navigator.geolocation) {
        try {
          const position = await new Promise((resolve, reject) => {
            navigator.geolocation.getCurrentPosition(resolve, reject, { timeout: 5000 });
          });
          startLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
        } catch (e) {
          console.log('Geolocation not available');
        }
      }
      
      const response = await fetch(`${API_BASE}/api/driver/orders/multi-route`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ 
          order_ids: orderIds,
          start_location: startLocation
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.detail || 'Routenberechnung fehlgeschlagen');
      }
      
      setOptimizedRoute(data);
      setShowRouteDialog(true);
    } catch (error) {
      toast.error(error.message);
    } finally {
      setLoadingRoute(false);
    }
  };

  // ==========================================================================
  // QR Scanner Handlers
  // ==========================================================================

  const handleScan = async (result) => {
    if (result && !scanning) {
      setScanning(true);
      const qrToken = result.text;
      setScanResult(qrToken);
      setScannerActive(false);
      
      try {
        // Extract order_id from token (JWT middle part contains order_id)
        const tokenParts = qrToken.split('.');
        if (tokenParts.length === 3) {
          const payload = JSON.parse(atob(tokenParts[1]));
          const orderId = payload.order_id;
          
          if (orderId) {
            await claimOrder(orderId, qrToken);
          } else {
            toast.error('Ungültiger QR-Code: Keine Bestellungs-ID');
          }
        } else {
          toast.error('Ungültiger QR-Code Format');
        }
      } catch (error) {
        console.error('QR scan error:', error);
        if (!error.message.includes('übernommen')) {
          toast.error('QR-Code konnte nicht verarbeitet werden');
        }
      } finally {
        setScanning(false);
        setScanResult(null);
      }
    }
  };

  // ==========================================================================
  // Navigation & Call Handlers
  // ==========================================================================

  const openGoogleMaps = (order) => {
    const address = order.delivery_address?.formatted_address || 
                   `${order.delivery_address?.street} ${order.delivery_address?.house_number}, ${order.delivery_address?.postal_code} ${order.delivery_address?.city}`;
    const mapsUrl = `https://www.google.com/maps/dir/?api=1&destination=${encodeURIComponent(address)}`;
    window.open(mapsUrl, '_blank');
  };

  const callCustomer = (phoneNumber) => {
    window.location.href = `tel:${phoneNumber}`;
  };

  const handleProblemSubmit = () => {
    if (problemOrderId && selectedProblemReason) {
      updateOrderStatus(problemOrderId, 'problem', selectedProblemReason);
      setShowProblemDialog(false);
      setProblemOrderId(null);
      setSelectedProblemReason('');
    }
  };

  // ==========================================================================
  // Render: Login Screen
  // ==========================================================================

  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-purple-600 via-purple-700 to-blue-700 flex items-center justify-center p-4">
        <Card className="w-full max-w-md bg-white/95 backdrop-blur" data-testid="flotto-login-card">
          <CardHeader className="text-center pb-2">
            <div className="mx-auto w-20 h-20 bg-gradient-to-br from-purple-500 to-blue-500 rounded-2xl flex items-center justify-center mb-4 shadow-lg">
              <Truck className="w-10 h-10 text-white" />
            </div>
            <CardTitle className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-blue-600 bg-clip-text text-transparent">
              flotto.app
            </CardTitle>
            <p className="text-gray-500 text-sm">Fahrer-Login</p>
          </CardHeader>
          
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="driver-id">Fahrer-ID</Label>
                <Input
                  id="driver-id"
                  value={driverId}
                  onChange={(e) => setDriverId(e.target.value)}
                  placeholder="z.B. fahrer-001"
                  required
                  data-testid="driver-id-input"
                  className="h-12"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="password">Passwort</Label>
                <Input
                  id="password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="driver-password-input"
                  className="h-12"
                />
              </div>
              
              <Button
                type="submit"
                className="w-full h-12 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-white font-bold"
                disabled={loginLoading}
                data-testid="driver-login-btn"
              >
                {loginLoading ? (
                  <RefreshCw className="w-5 h-5 animate-spin" />
                ) : (
                  <>
                    <LogIn className="w-5 h-5 mr-2" />
                    Anmelden
                  </>
                )}
              </Button>
            </form>
            
            <div className="mt-6 p-3 bg-gray-100 rounded-lg text-center text-sm text-gray-600">
              <p className="font-medium">Demo-Zugangsdaten:</p>
              <p>ID: <code className="bg-white px-1 rounded">fahrer-001</code></p>
              <p>Passwort: <code className="bg-white px-1 rounded">demo123</code></p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  // ==========================================================================
  // Render: Main Driver Dashboard
  // ==========================================================================

  return (
    <div className="min-h-screen bg-gray-100" data-testid="flotto-dashboard">
      {/* Header */}
      <header className="bg-gradient-to-r from-purple-600 to-blue-600 text-white p-4 shadow-lg sticky top-0 z-40">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <h1 className="text-xl font-bold">flotto.app</h1>
              <p className="text-xs text-purple-200">Hallo, {driverName}</p>
            </div>
          </div>
          
          <Button
            variant="ghost"
            size="sm"
            onClick={handleLogout}
            className="text-white hover:bg-white/20"
            data-testid="logout-btn"
          >
            <LogOut className="w-5 h-5" />
          </Button>
        </div>
      </header>

      <main className="max-w-lg mx-auto p-4 space-y-4">
        {/* Tabs: Aktiv / Historie */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="grid w-full grid-cols-2 h-12">
            <TabsTrigger value="active" className="text-base" data-testid="tab-active">
              <Package className="w-4 h-4 mr-2" />
              Aktiv
            </TabsTrigger>
            <TabsTrigger value="history" className="text-base" data-testid="tab-history">
              <History className="w-4 h-4 mr-2" />
              Historie
            </TabsTrigger>
          </TabsList>
          
          {/* ============================================ */}
          {/* TAB: Aktive Lieferungen */}
          {/* ============================================ */}
          <TabsContent value="active" className="space-y-4 mt-4">
            {/* QR Scanner Section */}
            <Card className="border-2 border-dashed border-purple-300 bg-purple-50">
              <CardContent className="py-6">
                <h2 className="text-lg font-bold text-center mb-4 text-purple-900">
                  Lieferung übernehmen
                </h2>
                
                {!scannerActive ? (
                  <Button
                    onClick={() => setScannerActive(true)}
                    className="w-full h-16 bg-gradient-to-r from-purple-600 to-blue-600 hover:from-purple-700 hover:to-blue-700 text-lg font-bold"
                    data-testid="open-scanner-btn"
                  >
                    <QrCode className="w-6 h-6 mr-3" />
                    QR-Code scannen
                  </Button>
                ) : (
                  <div className="space-y-3">
                    <div className="rounded-xl overflow-hidden border-2 border-purple-400">
                      <QrReader
                        onResult={handleScan}
                        constraints={{ facingMode: 'environment' }}
                        containerStyle={{ width: '100%' }}
                        videoStyle={{ borderRadius: '12px' }}
                      />
                    </div>
                    
                    {scanning && (
                      <div className="text-center text-purple-600 font-medium">
                        <RefreshCw className="w-5 h-5 animate-spin inline mr-2" />
                        Verarbeite QR-Code...
                      </div>
                    )}
                    
                    <Button
                      variant="outline"
                      onClick={() => setScannerActive(false)}
                      className="w-full"
                      data-testid="close-scanner-btn"
                    >
                      <X className="w-5 h-5 mr-2" />
                      Abbrechen
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Route Optimization Button */}
            {orders.length > 1 && (
              <Button
                onClick={optimizeRoute}
                disabled={loadingRoute}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 text-white"
                data-testid="optimize-route-btn"
              >
                {loadingRoute ? (
                  <RefreshCw className="w-5 h-5 animate-spin mr-2" />
                ) : (
                  <Route className="w-5 h-5 mr-2" />
                )}
                Route optimieren ({orders.length} Lieferungen)
              </Button>
            )}

            {/* My Deliveries Section */}
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-800">Meine Lieferungen</h2>
              <Button
                variant="outline"
                size="sm"
                onClick={loadMyOrders}
                disabled={loadingOrders}
                data-testid="refresh-orders-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-1 ${loadingOrders ? 'animate-spin' : ''}`} />
                Aktualisieren
              </Button>
            </div>

            {loadingOrders && orders.length === 0 ? (
              <Card className="py-8">
                <div className="text-center text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-500" />
                  Lade Lieferungen...
                </div>
              </Card>
            ) : orders.length === 0 ? (
              <Card className="py-8">
                <div className="text-center text-gray-500">
                  <Package className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Keine aktiven Lieferungen</p>
                  <p className="text-sm">Scanne einen QR-Code um eine Lieferung zu übernehmen</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-4" data-testid="orders-list">
                {orders.map((order, index) => (
                  <Card 
                    key={order.id || order._id} 
                    className="border-l-4 border-l-purple-500 shadow-md"
                    data-testid={`order-card-${order.id || order._id}`}
                  >
                    <CardContent className="p-4">
                      {/* Route Order Badge */}
                      <div className="flex items-center justify-between mb-3">
                        <div className="flex items-center gap-2">
                          <Badge className="bg-purple-100 text-purple-700 font-bold">
                            #{index + 1}
                          </Badge>
                          <Badge variant="outline" className="text-green-600 border-green-300">
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Übernommen
                          </Badge>
                        </div>
                        <span className="text-xs text-gray-500">
                          <Clock className="w-3 h-3 inline mr-1" />
                          {order.claimed_at ? new Date(order.claimed_at).toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' }) : '-'}
                        </span>
                      </div>
                      
                      {/* Customer Info */}
                      <div className="mb-4">
                        <h3 className="font-bold text-lg text-gray-900 flex items-center gap-2">
                          <User className="w-5 h-5 text-purple-500" />
                          {order.customer_name}
                        </h3>
                        
                        <div className="mt-2 space-y-1 text-sm text-gray-600">
                          <div className="flex items-start gap-2">
                            <MapPin className="w-4 h-4 text-purple-500 mt-0.5 flex-shrink-0" />
                            <span>{order.delivery_address?.formatted_address || 
                              `${order.delivery_address?.street} ${order.delivery_address?.house_number}, ${order.delivery_address?.postal_code} ${order.delivery_address?.city}`}
                            </span>
                          </div>
                          
                          {order.notes && (
                            <div className="bg-yellow-100 border border-yellow-300 rounded-lg p-2 mt-2">
                              <p className="text-yellow-800 font-medium text-xs">📝 Hinweis: {order.notes}</p>
                            </div>
                          )}
                        </div>
                      </div>
                      
                      {/* Order Items Summary */}
                      <div className="bg-gray-50 rounded-lg p-3 mb-4">
                        <p className="text-xs font-medium text-gray-500 mb-2">Artikel:</p>
                        <div className="space-y-1">
                          {order.items?.slice(0, 3).map((item, idx) => (
                            <div key={idx} className="text-sm flex justify-between">
                              <span>{item.qty || item.quantity || 1}x {item.name}</span>
                            </div>
                          ))}
                          {order.items?.length > 3 && (
                            <p className="text-xs text-gray-400">+ {order.items.length - 3} weitere</p>
                          )}
                        </div>
                        <div className="border-t mt-2 pt-2 flex justify-between font-bold text-green-700">
                          <span>Gesamt:</span>
                          <span>{new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(order.total_gross || 0)}</span>
                        </div>
                      </div>
                      
                      {/* Action Buttons */}
                      <div className="grid grid-cols-2 gap-2 mb-3">
                        <Button
                          onClick={() => callCustomer(order.customer_phone)}
                          className="bg-green-600 hover:bg-green-700 text-white"
                          data-testid={`call-btn-${order.id || order._id}`}
                        >
                          <Phone className="w-4 h-4 mr-2" />
                          Anrufen
                        </Button>
                        
                        <Button
                          onClick={() => openGoogleMaps(order)}
                          className="bg-blue-600 hover:bg-blue-700 text-white"
                          data-testid={`navigate-btn-${order.id || order._id}`}
                        >
                          <Navigation className="w-4 h-4 mr-2" />
                          Route
                        </Button>
                      </div>
                      
                      {/* Status Buttons */}
                      {order.status === 'dispatched' && (
                        <div className="space-y-2">
                          <Button
                            onClick={() => updateOrderStatus(order.id || order._id, 'delivered')}
                            className="w-full bg-purple-600 hover:bg-purple-700 text-white font-bold h-12"
                            data-testid={`delivered-btn-${order.id || order._id}`}
                          >
                            <CheckCircle className="w-5 h-5 mr-2" />
                            Zugestellt
                          </Button>
                          
                          <div className="grid grid-cols-2 gap-2">
                            <Button
                              variant="outline"
                              onClick={() => {
                                setProblemOrderId(order.id || order._id);
                                setShowProblemDialog(true);
                              }}
                              className="border-red-300 text-red-600 hover:bg-red-50"
                              data-testid={`problem-btn-${order.id || order._id}`}
                            >
                              <AlertTriangle className="w-4 h-4 mr-2" />
                              Problem
                            </Button>
                            
                            <Button
                              variant="outline"
                              onClick={() => {
                                setReleaseOrderId(order.id || order._id);
                                setShowReleaseDialog(true);
                              }}
                              className="border-orange-300 text-orange-600 hover:bg-orange-50"
                              data-testid={`release-btn-${order.id || order._id}`}
                            >
                              <Unlock className="w-4 h-4 mr-2" />
                              Freigeben
                            </Button>
                          </div>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
          
          {/* ============================================ */}
          {/* TAB: Historie */}
          {/* ============================================ */}
          <TabsContent value="history" className="space-y-4 mt-4">
            {/* Date Filter */}
            <Card>
              <CardContent className="p-4">
                <div className="flex items-center gap-2 mb-3">
                  <Filter className="w-5 h-5 text-gray-500" />
                  <span className="font-medium">Zeitraum filtern</span>
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-xs">Von</Label>
                    <Input
                      type="date"
                      value={historyDateFrom}
                      onChange={(e) => setHistoryDateFrom(e.target.value)}
                      className="h-10"
                      data-testid="history-date-from"
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Bis</Label>
                    <Input
                      type="date"
                      value={historyDateTo}
                      onChange={(e) => setHistoryDateTo(e.target.value)}
                      className="h-10"
                      data-testid="history-date-to"
                    />
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Stats Summary */}
            <div className="grid grid-cols-3 gap-3">
              <Card className="bg-green-50 border-green-200">
                <CardContent className="p-3 text-center">
                  <CheckCircle className="w-6 h-6 text-green-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-green-700">{historyStats.delivered}</p>
                  <p className="text-xs text-green-600">Zugestellt</p>
                </CardContent>
              </Card>
              <Card className="bg-red-50 border-red-200">
                <CardContent className="p-3 text-center">
                  <AlertTriangle className="w-6 h-6 text-red-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-red-700">{historyStats.problems}</p>
                  <p className="text-xs text-red-600">Probleme</p>
                </CardContent>
              </Card>
              <Card className="bg-blue-50 border-blue-200">
                <CardContent className="p-3 text-center">
                  <Euro className="w-6 h-6 text-blue-600 mx-auto mb-1" />
                  <p className="text-2xl font-bold text-blue-700">{historyStats.total_revenue.toFixed(0)}€</p>
                  <p className="text-xs text-blue-600">Umsatz</p>
                </CardContent>
              </Card>
            </div>
            
            {/* History List */}
            {loadingHistory ? (
              <Card className="py-8">
                <div className="text-center text-gray-500">
                  <RefreshCw className="w-8 h-8 animate-spin mx-auto mb-2 text-purple-500" />
                  Lade Historie...
                </div>
              </Card>
            ) : historyOrders.length === 0 ? (
              <Card className="py-8">
                <div className="text-center text-gray-500">
                  <History className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                  <p className="font-medium">Keine Lieferungen im gewählten Zeitraum</p>
                </div>
              </Card>
            ) : (
              <div className="space-y-3" data-testid="history-list">
                {historyOrders.map((order) => (
                  <Card 
                    key={order.id || order._id}
                    className={`border-l-4 ${
                      order.status === 'delivered' ? 'border-l-green-500' :
                      order.status === 'problem' ? 'border-l-red-500' : 'border-l-gray-400'
                    }`}
                  >
                    <CardContent className="p-3">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          {order.status === 'delivered' ? (
                            <Badge className="bg-green-100 text-green-700">
                              <CheckCircle className="w-3 h-3 mr-1" />
                              Zugestellt
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700">
                              <AlertTriangle className="w-3 h-3 mr-1" />
                              Problem
                            </Badge>
                          )}
                        </div>
                        <span className="text-xs text-gray-500">
                          {order.delivered_at || order.claimed_at ? 
                            new Date(order.delivered_at || order.claimed_at).toLocaleDateString('de-DE', { 
                              day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' 
                            }) : '-'}
                        </span>
                      </div>
                      
                      <div className="text-sm">
                        <p className="font-medium">{order.customer_name}</p>
                        <p className="text-gray-500 text-xs truncate">
                          {order.delivery_address?.formatted_address || 
                            `${order.delivery_address?.street || ''} ${order.delivery_address?.city || ''}`}
                        </p>
                      </div>
                      
                      <div className="flex justify-between items-center mt-2 pt-2 border-t">
                        <span className="text-xs text-gray-500">
                          {order.items?.length || 0} Artikel
                        </span>
                        <span className="font-bold text-green-700">
                          {new Intl.NumberFormat('de-DE', { style: 'currency', currency: 'EUR' }).format(order.total_gross || 0)}
                        </span>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </main>

      {/* Problem Dialog */}
      <Dialog open={showProblemDialog} onOpenChange={setShowProblemDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              Problem melden
            </DialogTitle>
            <DialogDescription>
              Wählen Sie den Grund für das Problem aus.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <Select value={selectedProblemReason} onValueChange={setSelectedProblemReason}>
              <SelectTrigger data-testid="problem-reason-select">
                <SelectValue placeholder="Grund auswählen..." />
              </SelectTrigger>
              <SelectContent>
                {PROBLEM_REASONS.map((reason) => (
                  <SelectItem key={reason.code} value={reason.code}>
                    {reason.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowProblemDialog(false);
                  setSelectedProblemReason('');
                }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={handleProblemSubmit}
                disabled={!selectedProblemReason}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white"
                data-testid="submit-problem-btn"
              >
                Problem melden
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Release Dialog (NEU) */}
      <Dialog open={showReleaseDialog} onOpenChange={setShowReleaseDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-600">
              <Unlock className="w-5 h-5" />
              Lieferung freigeben
            </DialogTitle>
            <DialogDescription>
              Die Lieferung wird für andere Fahrer freigegeben.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            <div>
              <Label>Grund *</Label>
              <Select value={selectedReleaseReason} onValueChange={setSelectedReleaseReason}>
                <SelectTrigger data-testid="release-reason-select">
                  <SelectValue placeholder="Grund auswählen..." />
                </SelectTrigger>
                <SelectContent>
                  {RELEASE_REASONS.map((reason) => (
                    <SelectItem key={reason.code} value={reason.code}>
                      {reason.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <Label>Details (optional)</Label>
              <Textarea
                value={releaseNotes}
                onChange={(e) => setReleaseNotes(e.target.value)}
                placeholder="Weitere Details..."
                className="h-20"
                data-testid="release-notes-input"
              />
            </div>
            
            <div className="flex gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowReleaseDialog(false);
                  setSelectedReleaseReason('');
                  setReleaseNotes('');
                }}
                className="flex-1"
              >
                Abbrechen
              </Button>
              <Button
                onClick={releaseOrder}
                disabled={!selectedReleaseReason}
                className="flex-1 bg-orange-600 hover:bg-orange-700 text-white"
                data-testid="submit-release-btn"
              >
                Freigeben
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Route Optimization Dialog (NEU) */}
      <Dialog open={showRouteDialog} onOpenChange={setShowRouteDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-blue-600">
              <Route className="w-5 h-5" />
              Optimierte Route
            </DialogTitle>
          </DialogHeader>
          
          {optimizedRoute && (
            <div className="space-y-4">
              {/* Summary */}
              <div className="grid grid-cols-3 gap-2 text-center">
                <div className="bg-blue-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-blue-700">{optimizedRoute.summary?.total_deliveries}</p>
                  <p className="text-xs text-blue-600">Lieferungen</p>
                </div>
                <div className="bg-green-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-green-700">{optimizedRoute.summary?.total_distance_km} km</p>
                  <p className="text-xs text-green-600">Strecke</p>
                </div>
                <div className="bg-purple-50 rounded-lg p-2">
                  <p className="text-lg font-bold text-purple-700">~{optimizedRoute.summary?.estimated_total_minutes} min</p>
                  <p className="text-xs text-purple-600">Fahrzeit</p>
                </div>
              </div>
              
              {/* Route List */}
              <div className="space-y-2 max-h-60 overflow-y-auto">
                {optimizedRoute.optimized_route?.map((stop, idx) => (
                  <div key={stop.order_id} className="flex items-center gap-3 p-2 bg-gray-50 rounded-lg">
                    <div className="w-8 h-8 rounded-full bg-purple-600 text-white flex items-center justify-center font-bold">
                      {stop.route_order}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{stop.customer_name}</p>
                      <p className="text-xs text-gray-500 truncate">{stop.address}</p>
                    </div>
                    <div className="text-right text-xs">
                      <p className="text-blue-600">{stop.distance_km} km</p>
                      <p className="text-gray-500">~{stop.estimated_minutes} min</p>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Open in Maps */}
              <Button
                onClick={() => window.open(optimizedRoute.maps_url, '_blank')}
                className="w-full bg-blue-600 hover:bg-blue-700 text-white"
              >
                <ExternalLink className="w-4 h-4 mr-2" />
                In Google Maps öffnen
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Claim Success Dialog */}
      <Dialog open={showClaimDialog} onOpenChange={setShowClaimDialog}>
        <DialogContent className="max-w-sm">
          <div className="text-center py-4">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-10 h-10 text-green-600" />
            </div>
            <DialogTitle className="text-xl text-green-700 mb-2">
              Lieferung übernommen!
            </DialogTitle>
            {claimedOrder && (
              <div className="space-y-2 text-left bg-gray-50 rounded-lg p-4 mt-4">
                <p className="font-bold text-lg">{claimedOrder.customer_name}</p>
                <p className="text-sm text-gray-600 flex items-start gap-2">
                  <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  {claimedOrder.delivery_address?.formatted_address}
                </p>
                {claimedOrder.notes && (
                  <div className="bg-yellow-100 border border-yellow-300 rounded p-2 mt-2">
                    <p className="text-yellow-800 text-xs">📝 {claimedOrder.notes}</p>
                  </div>
                )}
              </div>
            )}
          </div>
          
          <div className="grid grid-cols-2 gap-2">
            {claimedOrder && (
              <>
                <Button
                  onClick={() => {
                    callCustomer(claimedOrder.customer_phone);
                    setShowClaimDialog(false);
                  }}
                  className="bg-green-600 hover:bg-green-700"
                >
                  <Phone className="w-4 h-4 mr-2" />
                  Anrufen
                </Button>
                <Button
                  onClick={() => {
                    openGoogleMaps(claimedOrder);
                    setShowClaimDialog(false);
                  }}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Navigation className="w-4 h-4 mr-2" />
                  Route
                </Button>
              </>
            )}
          </div>
          
          <Button
            variant="outline"
            onClick={() => setShowClaimDialog(false)}
            className="w-full mt-2"
          >
            Schließen
          </Button>
        </DialogContent>
      </Dialog>
    </div>
  );
}
